import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../../components/domain/Bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { api, toastError } from "../../lib/api";
import { useEffectiveRole } from "../../lib/roles";

type LabelRow = {
  id: string;
  code: string;
  label: string;
  guidance?: string;
  canonical_stage?: string;
  order?: number;
};

type WorkflowConfig = {
  version: number;
  terminology: LabelRow[];
  stages: LabelRow[];
  outcomes: LabelRow[];
  required_fields: Record<string, string[]>;
  require_visit_verification: boolean;
  required_field_catalog: Record<string, string[]>;
  protected: { canonical_stage_codes: string[]; message: string };
};

const humanize = (value: string) =>
  value.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function WorkflowEditor() {
  const role = useEffectiveRole();
  const canWrite = role === "owner_admin";
  const [config, setConfig] = useState<WorkflowConfig | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get<WorkflowConfig>("/tenant/workflow-editor");
      setConfig(data);
    } catch (error) {
      toastError(error, "Could not load workflow configuration.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateLabel = (
    section: "terminology" | "stages" | "outcomes",
    code: string,
    field: "label" | "guidance",
    value: string,
  ) => setConfig((current) => current && ({
    ...current,
    [section]: current[section].map((row) =>
      row.code === code ? { ...row, [field]: value } : row),
  }));

  const toggleRequired = (entity: string, field: string) =>
    setConfig((current) => {
      if (!current) return current;
      const selected = current.required_fields[entity] || [];
      const next = selected.includes(field)
        ? selected.filter((item) => item !== field)
        : [...selected, field];
      return {
        ...current,
        required_fields: { ...current.required_fields, [entity]: next },
      };
    });

  const save = async () => {
    if (!config) return;
    setBusy(true);
    try {
      const payload = {
        version: config.version,
        terminology: config.terminology.map(({ code, label }) => ({ code, label })),
        stages: config.stages.map(({ code, label, guidance }) => ({ code, label, guidance: guidance || "" })),
        outcomes: config.outcomes.map(({ code, label }) => ({ code, label })),
        required_fields: config.required_fields,
        require_visit_verification: config.require_visit_verification,
      };
      const { data } = await api.patch<WorkflowConfig>("/tenant/workflow-editor", payload);
      setConfig(data);
      window.dispatchEvent(new Event("remedikeep:terminology-saved"));
      toast.success("Terminology and workflow saved");
    } catch (error) {
      toastError(error, "Could not save workflow configuration.");
    } finally {
      setBusy(false);
    }
  };

  if (!config) {
    return (
      <>
        <PageHeader title="Terminology & Workflow" subtitle="Tenant-facing labels with protected system logic" />
        <div className="p-12 grid place-items-center text-sm text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mb-2" /> Loading…
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Terminology & Workflow"
        subtitle={canWrite ? "Customize what your team sees" : "Read-only workflow configuration"}
      />
      <div className="max-w-5xl mx-auto p-6 md:p-8 grid gap-6" data-testid="workflow-editor-page">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 flex gap-3 text-sm text-emerald-900">
          <ShieldCheck className="w-5 h-5 shrink-0" />
          <div>
            <div className="font-semibold">Your reporting logic stays protected</div>
            <div className="mt-1">{config.protected.message}</div>
          </div>
        </div>

        <EditorSection title="Business terminology" description="Rename tenant-facing nouns without changing their permanent codes.">
          {config.terminology.map((row) => (
            <LabelEditor key={row.code} row={row} disabled={!canWrite || busy}
              onLabel={(value) => updateLabel("terminology", row.code, "label", value)} />
          ))}
        </EditorSection>

        <EditorSection title="Pipeline stages" description="The eight stages and their order are locked; labels and guidance are editable.">
          {config.stages.map((row) => (
            <div key={row.code} className="grid md:grid-cols-[180px_1fr_1.5fr] gap-3 items-start border-t border-slate-100 py-3 first:border-t-0">
              <CodeBadge code={`${row.order}. ${row.code}`} />
              <Input value={row.label} disabled={!canWrite || busy}
                onChange={(e) => updateLabel("stages", row.code, "label", e.target.value)} />
              <Textarea value={row.guidance || ""} disabled={!canWrite || busy} rows={2}
                aria-label={`${row.code} guidance`}
                onChange={(e) => updateLabel("stages", row.code, "guidance", e.target.value)} />
            </div>
          ))}
        </EditorSection>

        <EditorSection title="Outcome labels" description="Customize visible outcome names while keeping their codes stable for analytics.">
          {config.outcomes.map((row) => (
            <LabelEditor key={row.code} row={row} disabled={!canWrite || busy}
              onLabel={(value) => updateLabel("outcomes", row.code, "label", value)} />
          ))}
        </EditorSection>

        <EditorSection title="Visit verification policy" description="Control whether staff-reported completion needs a second approval before reimbursement eligibility.">
          <label className="flex items-start gap-3 rounded-md border p-4 text-sm">
            <input type="checkbox" className="mt-1" disabled={!canWrite || busy}
              checked={config.require_visit_verification}
              onChange={(e) => setConfig({ ...config, require_visit_verification: e.target.checked })}
              data-testid="require-visit-verification" />
            <span><strong>Require visit verification</strong><span className="mt-1 block text-slate-500">
              Off: completed assigned activities qualify immediately. On: completion waits for an authorized reviewer before becoming reimbursement-eligible.
            </span></span>
          </label>
        </EditorSection>

        <EditorSection title="Required fields" description="Choose which existing fields users must complete in each workflow.">
          {Object.entries(config.required_field_catalog).map(([entity, fields]) => (
            <fieldset key={entity} className="border-t border-slate-100 py-3 first:border-t-0">
              <legend className="font-medium text-sm text-slate-800 mb-2">{humanize(entity)}</legend>
              <div className="flex flex-wrap gap-3">
                {fields.map((field) => (
                  <label key={field} className="flex items-center gap-2 text-sm text-slate-600">
                    <input type="checkbox" disabled={!canWrite || busy}
                      checked={(config.required_fields[entity] || []).includes(field)}
                      onChange={() => toggleRequired(entity, field)} />
                    {humanize(field)}
                  </label>
                ))}
              </div>
            </fieldset>
          ))}
        </EditorSection>

        {canWrite && (
          <div className="flex justify-end">
            <Button onClick={save} disabled={busy} data-testid="workflow-save">
              {busy ? "Saving…" : "Save terminology & workflow"}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

function EditorSection({ title, description, children }: {
  title: string; description: string; children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5">
      <h2 className="font-semibold text-slate-900">{title}</h2>
      <p className="text-sm text-slate-500 mt-1 mb-4">{description}</p>
      {children}
    </section>
  );
}

function CodeBadge({ code }: { code: string }) {
  return <code className="text-xs text-slate-500 bg-slate-50 border rounded px-2 py-1">{code}</code>;
}

function LabelEditor({ row, disabled, onLabel }: {
  row: LabelRow; disabled: boolean; onLabel: (value: string) => void;
}) {
  return (
    <div className="grid md:grid-cols-[180px_1fr] gap-3 items-center border-t border-slate-100 py-3 first:border-t-0">
      <CodeBadge code={row.code} />
      <div>
        <Label htmlFor={`workflow-${row.code}`} className="sr-only">{humanize(row.code)}</Label>
        <Input id={`workflow-${row.code}`} value={row.label} disabled={disabled}
          onChange={(e) => onLabel(e.target.value)} />
      </div>
    </div>
  );
}
